import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { Api } from '@/api';
import type { PipelineReviewContactConfigurationEntity } from '@/api/entity_types/pipeline_review/ContactConfiguration';

export const useTeammatePipelineReviews = () => {
  const baseConfig = ref<PipelineReviewContactConfigurationEntity | null>(null);
  const optimisticUpdates = ref<Partial<PipelineReviewContactConfigurationEntity>>({});
  const pendingKeys: Set<string>[] = [];

  const config = computed(() => {
    if (!baseConfig.value) return null;
    return { ...baseConfig.value, ...optimisticUpdates.value };
  });

  const setBaseConfig = (data: PipelineReviewContactConfigurationEntity | null) => {
    baseConfig.value = data;
    optimisticUpdates.value = {};
  };

  const clearResolvedUpdates = () => {
    const stillPending = new Set(pendingKeys.flatMap(s => [...s]));
    const remaining: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(optimisticUpdates.value)) {
      if (stillPending.has(k)) remaining[k] = v;
    }
    optimisticUpdates.value = remaining as Partial<PipelineReviewContactConfigurationEntity>;
  };

  const updateField = async (params: Partial<PipelineReviewContactConfigurationEntity>) => {
    if (!baseConfig.value?.id) return;
    const keys = new Set(Object.keys(params));
    pendingKeys.push(keys);

    optimisticUpdates.value = { ...optimisticUpdates.value, ...params };

    const response = await Api.PipelineReviewContactConfigurations.updateContactConfiguration(baseConfig.value.id, params);
    pendingKeys.splice(pendingKeys.indexOf(keys), 1);

    if (response.status === 'success') {
      baseConfig.value = { ...baseConfig.value, ...params };
      clearResolvedUpdates();
      toast.success('Pipeline review configuration updated');
    } else {
      clearResolvedUpdates();
      toast.error('Update failed', {
        description: response.messages?.join(', ') || 'Failed to update pipeline review configuration',
      });
    }
  };

  return { config, setBaseConfig, updateField };
};
